import 'react';
import {Box, Skeleton, Divider} from '@mui/material';

export type LoanWpSkeletonVariant = 'bank' | 'vehicle';

export type LoanWpSkeletonProps = {
    variant?: LoanWpSkeletonVariant;
};

/**
 * Loading skeleton aligned with BankLoan / VehicleLoan layout after framework update:
 * WpHeader → WpCheckToolbar (issue mark + 3 per-checks) → horizontal loan table → SME-FRS.
 */
export default function LoanWpSkeleton({variant = 'bank'}: LoanWpSkeletonProps) {
    const isBank = variant === 'bank';
    // Identity cols + amount cols + actions
    // Bank: Bank, Loan ac no | Current, Non-current, Interest, b/f, Newly, Repayment, c/f | +
    // Vehicle: MV, Agreement | Current, Non-current, Interest, Purchase, Down, HP, Term, Instalment | actions
    const amountColCount = isBank ? 7 : 8;
    const identityColCount = 2;
    const dataRowCount = isBank ? 3 : 3;
    const colWidths = isBank
        ? [90, 80, 88, 100, 72, 56, 110, 100, 56, 56]
        : [90, 90, 72, 88, 72, 72, 80, 64, 48, 90, 48];

    const totalCols = identityColCount + amountColCount + 1;

    return (
        <Box sx={{width: '100%'}}>
            {/* WpHeader */}
            <Box sx={{mb: 2}}>
                <Skeleton variant="text" width="35%" height={24} sx={{mb: 0.5}}/>
                <Box sx={{display: 'flex', gap: 4, mb: 1}}>
                    <Skeleton variant="text" width="20%" height={18}/>
                    <Skeleton variant="text" width="25%" height={18}/>
                </Box>
                <Box sx={{display: 'flex', gap: 4}}>
                    <Skeleton variant="text" width="18%" height={18}/>
                    <Skeleton variant="text" width="22%" height={18}/>
                </Box>
            </Box>

            <Divider sx={{mb: 2}}/>

            {/* WpCheckToolbar: issue mark left + 3 per-check cards */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Box sx={{minWidth: 160}}>
                    <Skeleton variant="rounded" width={140} height={32} sx={{mb: 1}}/>
                    <Skeleton variant="text" width={120} height={14}/>
                </Box>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap', flex: 1}}>
                    {[1, 2, 3].map((i) => (
                        <Box
                            key={i}
                            sx={{
                                minWidth: 260,
                                maxWidth: 320,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1.5,
                            }}
                        >
                            <Skeleton variant="text" width="55%" height={16} sx={{mb: 1}}/>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
                                <Skeleton variant="text" width="28%" height={14}/>
                                <Skeleton variant="text" width="28%" height={14}/>
                            </Box>
                            {[1, 2, 3].map((j) => (
                                <Box key={j} sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
                                    <Skeleton variant="text" width="40%" height={14}/>
                                    <Skeleton variant="text" width="25%" height={14}/>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Divider sx={{mb: 2}}/>

            {/* Horizontal loan table (cols = loans metrics; rows = loan entries) */}
            <Box sx={{width: 'fit-content', maxWidth: '100%', overflowX: 'auto', mb: 2}}>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        minWidth: isBank ? 780 : 900,
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1,
                            py: 1,
                            bgcolor: 'action.hover',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {Array.from({length: totalCols}).map((_, i) => (
                            <Skeleton
                                key={`h-${i}`}
                                variant="text"
                                width={colWidths[i] ?? 72}
                                height={20}
                                sx={{flexShrink: 0}}
                            />
                        ))}
                    </Box>

                    {/* Data rows */}
                    {Array.from({length: dataRowCount}).map((_, rowIdx) => (
                        <Box
                            key={`r-${rowIdx}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1,
                                py: 0.75,
                                borderTop: rowIdx === 0 ? undefined : '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            {Array.from({length: totalCols}).map((_, i) => (
                                <Box
                                    key={`c-${rowIdx}-${i}`}
                                    sx={{
                                        width: colWidths[i] ?? 72,
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.25,
                                    }}
                                >
                                    <Skeleton variant="text" width="85%" height={18}/>
                                    {/* Adjust icons on Current / Non-current / Interest */}
                                    {i >= identityColCount && i < identityColCount + 3 && (
                                        <Skeleton variant="circular" width={18} height={18}/>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ))}

                    {/* Total row (+ optional adj totals under first 3 amount cols) */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            px: 1,
                            py: 1,
                            borderTop: '2px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {Array.from({length: totalCols}).map((_, i) => (
                            <Box
                                key={`t-${i}`}
                                sx={{
                                    width: colWidths[i] ?? 72,
                                    flexShrink: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 0.25,
                                }}
                            >
                                <Skeleton variant="text" width="90%" height={18}/>
                                {i >= identityColCount && i < identityColCount + 3 && (
                                    <Skeleton variant="rounded" width="70%" height={16}/>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* HKFRS SME-FRS accordion placeholder */}
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: 'action.hover',
                    }}
                >
                    <Skeleton variant="circular" width={18} height={18}/>
                    <Skeleton variant="text" width={140} height={18}/>
                </Box>
            </Box>
        </Box>
    );
}
